import { memo, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  CircularProgress,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as CcfoliaProLogo } from "./CcfoliaProLogo.svg";
import { ReactComponent as SoundMasterLogo } from "./SoundMasterLogo.svg";
import {
  createSubscription,
  joinSubscription,
  joinSoundMaster,
  getSoundMasterPrice,
} from "api";
import { getStripe } from "modules/stripe";
import theme from "theme";
import SoundMasterJoinConfirmDialog from "containers/SoundMasterJoinConfirmDialog";
import { patchedAppUser } from "stores/modules/app.user/operations";
import { auth } from "initializer";

const PlanSelect = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();

  const open = useAppSelector((state) =>
    store.getAppState(state, "openPlanSelect")
  );

  const plan = useAppSelector((state) => store.getAppState(state, "plan"));
  const isPro = plan === "ccfolia-pro";
  const isSoundMaster = useAppSelector((state) =>
    store.getAppState(state, "isSoundMaster")
  );

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [soundMasterPrice, setSoundMasterPrice] = useState<number | null>(null);
  const [campaignPrice, setCampaignPrice] = useState<number | null>(null);
  const [campaignLabel, setCampaignLabel] = useState<string | null>(null);

  useEffect(() => {
    getSoundMasterPrice().then((data) => {
      setSoundMasterPrice(data.price);
      setCampaignPrice(data.campaignPrice ?? null);
      setCampaignLabel(data.campaignLabel ?? null);
    });
  }, []);

  const onClose = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        if (state.destOnClosePlanSelect === "userSettings") {
          state.openUserSettings = true;
        }
        state.openPlanSelect = false;
      })
    );
  }, [dispatch]);

  const onClickUpgrade = async () => {
    if (auth.currentUser == null || auth.currentUser.isAnonymous) {
      window.alert(t("CCFOLIA PROに加入するには、ログインしてください。"));
      return;
    }

    const { url } = await createSubscription();
    window.location.href = url;
  };

  const onClickUpgradeWithSoundMaster = async () => {
    if (auth.currentUser == null || auth.currentUser.isAnonymous) {
      window.alert(
        t("CCFOLIA PRO + SOUND MASTER に加入するには、ログインしてください。")
      );
      return;
    }

    // If already Pro, show confirmation dialog before adding Sound Master
    if (isPro) {
      setOpenConfirmDialog(true);
    } else {
      // Not Pro yet, join both Pro and Sound Master via Stripe
      const { url } = await joinSubscription(["pro", "sound-master"]);
      window.location.href = url;
    }
  };

  const handleConfirmJoinSoundMaster = async () => {
    setConfirmLoading(true);
    try {
      const res = await joinSoundMaster(soundMasterPrice ?? 880, undefined);

      if (res.status === "failure") {
        window.alert(t("加入に失敗しました"));
        setConfirmLoading(false);
        return;
      }

      if (res.status === "expired_preview") {
        window.alert(t("プレビューの有効期限が切れました"));
        return;
      }

      if (res.status === "active") {
        dispatch(patchedAppUser({ isSoundMaster: true }));
        setOpenConfirmDialog(false);
        setConfirmLoading(false);
        window.location.href = "/home?success";
        return;
      }

      const stripe = await getStripe();
      const result = await stripe.confirmCardPayment(res.clientSecret);

      if (result.error) {
        window.alert(t("カードのセキュリティチェックでエラーが発生しました"));
        console.log(result.error);
        setConfirmLoading(false);
        return;
      }

      if (result.paymentIntent.status !== "succeeded") {
        window.alert(t("カードのセキュリティチェックでエラーが発生しました"));
        setConfirmLoading(false);
        return;
      }

      dispatch(patchedAppUser({ isSoundMaster: true }));
      setOpenConfirmDialog(false);
      setConfirmLoading(false);
      window.location.href = "/home?success";
    } catch (error) {
      console.error(error);
      window.alert(t("エラーが発生しました"));
      setConfirmLoading(false);
    }
  };

  const handleCloseConfirmDialog = () => {
    if (!confirmLoading) {
      setOpenConfirmDialog(false);
    }
  };

  const effectivePrice = campaignPrice ?? soundMasterPrice ?? 880;
  const totalPrice = 500 + effectivePrice;
  const hasCampaign = campaignPrice !== null;

  return (
    <>
      <SoundMasterJoinConfirmDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmJoinSoundMaster}
        loading={confirmLoading}
        soundMasterPrice={soundMasterPrice ?? undefined}
        campaignPrice={campaignPrice ?? undefined}
        campaignLabel={campaignLabel ?? undefined}
      />
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle>
          {t("プランの変更")}
          <IconButton
            aria-label={t("閉じる")}
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item md={6} sm={6} xs={12}>
              <Card
                variant="outlined"
                style={{
                  borderColor: theme.palette.secondary.main,
                  height: "100%",
                  background: theme.palette.grey[900],
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  style={{
                    background: theme.palette.background.default,
                  }}
                >
                  <LogoWrapper>
                    <CcfoliaProLogo role="img" aria-label="CCFOLIA PRO" />
                  </LogoWrapper>
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    color="textSecondary"
                  >
                    {t("ココフォリアのプロになれる新プラン")}
                  </Typography>
                </CardContent>
                <PriceArea>
                  <Typography variant="h4" align="center">
                    <Typography variant="caption">{t("月々")}</Typography> 500{" "}
                    <Typography variant="caption">{t("円")}</Typography>
                  </Typography>
                </PriceArea>
                <CardActions>
                  <ButtonLoading
                    variant="contained"
                    color="inherit"
                    onClick={onClickUpgrade}
                    disabled={isPro}
                  >
                    {isPro ? t("加入済み") : t("アップグレード")}
                  </ButtonLoading>
                </CardActions>
              </Card>
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Card
                variant="outlined"
                style={{
                  borderColor: theme.palette.secondary.main,
                  height: "100%",
                  background: theme.palette.grey[900],
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  style={{
                    background: theme.palette.background.default,
                  }}
                >
                  <SoundMasterLogoWrapper>
                    <CombinedLogoWrapper>
                      <CcfoliaProLogo role="img" aria-label="CCFOLIA PRO" />
                      <SoundMasterLogo role="img" aria-label="SOUND MASTER" />
                    </CombinedLogoWrapper>
                  </SoundMasterLogoWrapper>
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    color="textSecondary"
                  >
                    {t("PROよりお得な超プラン")}
                  </Typography>
                </CardContent>
                <PriceArea>
                  {hasCampaign && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      align="center"
                      display="block"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {t("通常価格")} ¥{500 + (soundMasterPrice ?? 880)}/
                      {t("月")}
                    </Typography>
                  )}
                  <Typography variant="h4" align="center">
                    <Typography variant="caption">{t("月々")}</Typography>{" "}
                    {totalPrice}{" "}
                    <Typography variant="caption">{t("円")}</Typography>
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                    display="block"
                  >
                    ¥500 + ¥{effectivePrice}
                  </Typography>
                  {/* 一旦、ラベルの内容は無視する */}
                  {hasCampaign && campaignLabel && (
                    <Typography
                      variant="caption"
                      color="secondary"
                      align="center"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {t("キャンペーン価格（初月のみ）")}
                    </Typography>
                  )}
                </PriceArea>
                <CardActions>
                  <ButtonLoading
                    variant="contained"
                    color="secondary"
                    onClick={onClickUpgradeWithSoundMaster}
                    disabled={isPro && isSoundMaster}
                  >
                    {isPro && isSoundMaster
                      ? t("加入済み")
                      : t("アップグレード")}
                  </ButtonLoading>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
          {isPro ? (
            <CancelInfoWrapper>
              <Typography
                variant="caption"
                color="textSecondary"
                align="center"
              >
                {t(
                  "サブスクリプションのキャンセルはユーザーメニューの「購入履歴」より行うことができます。"
                )}
                {isSoundMaster &&
                  t(
                    "プランの変更を行うには、サブスクリプションの停止後に、再度加入手続きを行なってください。"
                  )}
              </Typography>
            </CancelInfoWrapper>
          ) : (
            <CancelInfoWrapper>
              <Typography
                variant="caption"
                color="textSecondary"
                align="center"
                display="block"
                sx={{ mb: 1 }}
              >
                {t(
                  "毎月のサブスクリプションの請求は加入日を基準に行われます。"
                )}
                {t(
                  "今すぐアップグレードして、より快適なセッション体験を手に入れよう！"
                )}
              </Typography>
            </CancelInfoWrapper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="outlined"
            href="/plans"
            target="_blank"
            fullWidth
            endIcon={<OpenInNewIcon />}
          >
            {t("プランについての詳細はこちら")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

type ButtonLoadingProps = {
  variant: "outlined" | "contained";
  onClick: () => Promise<void>;
  children: ReactNode;
  color?: "inherit" | "secondary";
  disabled?: boolean;
};

const ButtonLoading = ({
  variant,
  onClick,
  children,
  color,
  disabled,
}: ButtonLoadingProps) => {
  const [loading, setLoading] = useState(false);

  if (loading) {
    return (
      <Button variant="contained" fullWidth disabled>
        <CircularProgress color="primary" size={24.5} />
      </Button>
    );
  }

  const handleClick = () => {
    setLoading(true);
    onClick().finally(() => setLoading(false));
  };

  return (
    <Button
      variant={variant}
      color={color}
      fullWidth
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

const CancelInfoWrapper = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 12px;
  margin-bottom: 4px;
`;

const PriceArea = styled(CardContent)`
  background: ${theme.palette.grey[900]};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;

  padding: 4px 0;

  svg {
    height: 16px;
  }
`;

const SoundMasterLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  margin-bottom: 8px;
`;

const CombinedLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  min-height: 48px;

  padding: 4px 0;

  svg:first-child {
    height: 16px;
  }

  svg:last-child {
    height: auto;
    max-width: 90px;
  }
`;

export default memo(PlanSelect);
